import { useEffect, type ReactNode } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";

import { useSession } from "../api/session";
import { setPendingPath } from "./pendingPath";

/**
 * The outermost guard, and now two questions rather than one: **who** (a session) and **where**
 * (a tenant, decision F-D3). Both read the SEAM (`useSession`), never `uiStore` or MSAL
 * directly; the seam's swap from dev personas to MSAL (AU-D13) did not change this file.
 *
 * The order matters. A tenant cannot be chosen before there is a token to ask `GET /me/tenants`
 * with, so an anonymous visitor goes to `/login` and only a signed-in one can be sent to
 * `/select-tenant`. Both remember where they were going (`pendingPath`), so a deep link survives
 * the detour.
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const { status, tenantKey } = useSession();
  const navigate = useNavigate();
  const location = useRouterState({ select: (s) => s.location.href });

  useEffect(() => {
    if (status === "anonymous") {
      setPendingPath(location);
      void navigate({ to: "/login", replace: true });
      return;
    }
    if (!tenantKey) {
      setPendingPath(location);
      // `/select-tenant` selects a single membership itself and continues, so a user with one
      // tenant never sees a picker -- they see a redirect they cannot perceive.
      void navigate({ to: "/select-tenant", replace: true });
    }
  }, [status, tenantKey, location, navigate]);

  if (status === "anonymous" || !tenantKey) return null;
  return <>{children}</>;
}
